import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { externalContactKeys } from '../query-keys';
import {
  addExternalContactContract,
  updateExternalContactContract,
  deleteExternalContactContract,
  type AddExternalContactRequest,
  type UpdateExternalContactRequest,
  type AddExternalContactResponse,
  type UpdateExternalContactResponse,
  type DeleteExternalContactResponse
} from '@bliks-io/contracts';

/**
 * Create external contact mutation
 */
export function useCreateExternalContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AddExternalContactRequest): Promise<AddExternalContactResponse> => {
      const { data: response } = await apiClient.post<AddExternalContactResponse>(
        addExternalContactContract.path,
        data
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: externalContactKeys.lists() });
    }
  });
}

/**
 * Update external contact mutation
 */
export function useUpdateExternalContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data
    }: {
      id: string;
      data: UpdateExternalContactRequest;
    }): Promise<UpdateExternalContactResponse> => {
      const path = updateExternalContactContract.path.replace(':id', id);
      const { data: response } = await apiClient.patch<UpdateExternalContactResponse>(path, data);
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: externalContactKeys.lists() });
      queryClient.invalidateQueries({ queryKey: externalContactKeys.detail(variables.id) });
    }
  });
}

/**
 * Delete external contact mutation
 */
export function useDeleteExternalContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<DeleteExternalContactResponse> => {
      const path = deleteExternalContactContract.path.replace(':id', id);
      const { data } = await apiClient.delete<DeleteExternalContactResponse>(path);
      return data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: externalContactKeys.lists() });
      queryClient.removeQueries({ queryKey: externalContactKeys.detail(id) });
    }
  });
}
